import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { FilterCategoryItemProps, RangeModeFilterConfig } from '@/components/data-table/filters-v2';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { RangeMode } from '@/types/filters';

import {
  parseRangeModeFilterValue,
  serializeRangeModeFilterValue,
} from '../utils/range-mode-filter-value';

/**
 * Submenu content for a range-mode filter (More than / Less than / Equal to + amount).
 * Stores its value as a single `"mode:amount"` string (`"mode:amount:strict"` when the
 * optional including toggle is off — see utils/range-mode-filter-value.ts).
 */
export function RangeModeFilterSubmenu({
  config,
  activeValues,
  onFilterChange,
  onClearFilter,
  onCloseSubmenu,
}: FilterCategoryItemProps & {
  config: RangeModeFilterConfig;
  onCloseSubmenu?: () => void;
}) {
  const { t } = useTranslation('common');
  const [selected, setSelected] = useState<RangeMode | ''>('');
  const [amount, setAmount] = useState('');
  const [isInclusive, setIsInclusive] = useState(true);

  // Seed local state from the active value (e.g. "more:50" / "more:50:strict")
  const currentValue = activeValues[0] ?? '';
  useEffect(() => {
    const parsed = parseRangeModeFilterValue(currentValue);
    if (parsed) {
      setSelected(parsed.mode);
      setAmount(parsed.amount);
      setIsInclusive(parsed.isInclusive);
    } else {
      setSelected('');
      setAmount('');
      setIsInclusive(true);
    }
  }, [currentValue]);

  const handleApply = () => {
    if (selected && amount) {
      onFilterChange(config.name, [
        serializeRangeModeFilterValue({ mode: selected, amount, isInclusive }),
      ]);
    }
    onCloseSubmenu?.();
  };

  const handleClear = () => {
    setSelected('');
    setAmount('');
    setIsInclusive(true);
    onClearFilter(config.name);
    onCloseSubmenu?.();
  };

  // Equal is inclusive by definition — the toggle only makes sense for More/Less
  const showInclusiveToggle =
    !!config.inclusiveToggle && selected !== '' && selected !== RangeMode.Equal;

  return (
    <div className="w-[240px] space-y-3 p-3">
      <RadioGroup value={selected} onValueChange={val => setSelected(val as RangeMode)}>
        {config.modes.map(({ label, value: mode }) => (
          <div key={mode} className="flex items-center gap-2">
            <RadioGroupItem value={mode} id={`${config.name}-${mode}`} />
            <label htmlFor={`${config.name}-${mode}`} className="flex-1 text-sm">
              {label}
            </label>
            <div className="relative w-24">
              <Input
                type="number"
                min={config.min ?? 0}
                max={config.max}
                placeholder={config.placeholder}
                disabled={selected !== mode}
                value={selected === mode ? amount : ''}
                onChange={e => setAmount(e.target.value)}
                // Hide native number input spinner arrows
                className="h-8 pr-5 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
              {config.unit && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  {config.unit}
                </span>
              )}
            </div>
          </div>
        ))}
      </RadioGroup>

      {showInclusiveToggle && (
        <div className="flex items-center gap-2 border-t pt-2">
          <Checkbox
            id={`${config.name}-inclusive`}
            checked={isInclusive}
            onCheckedChange={checked => setIsInclusive(checked === true)}
          />
          <label htmlFor={`${config.name}-inclusive`} className="text-sm">
            {t('tableFilters.rangeIncluding')}
          </label>
        </div>
      )}

      <div className="flex items-center justify-between pt-1">
        <Button size="sm" type="button" variant="ghost" className="h-7" onClick={handleClear}>
          {t('filters.clearAll')}
        </Button>
        <Button size="sm" className="h-7" onClick={handleApply} disabled={!selected || !amount}>
          {t('filters.apply')}
        </Button>
      </div>
    </div>
  );
}
